export default {
    chat: {
    // Title
        title: 'Chat',
        hiroTitle: 'Hiro',
        hiroSubtitle: 'Drafting Assistant',
        taskProcessing: 'Task Processing',
        planDefaultTitle: 'Execution Plan',
        planStepFallback: 'Step {index}',
        planTypes: {
            Free: 'Free',
            Internal: 'Internal',
            Pro: 'Pro',
        },

        // Welcome page
        welcome: {
            title: 'Hello! I\'m Hiro',
        },
        hiroIntro: 'Your drafting assistant, focused on helping you efficiently complete patent application documents.',
        disclaimer: 'AI-generated for reference only',

        // Role
        role: {
            user: 'You',
            assistant: 'AI Assistant',
            system: 'System',
        },

        // Status
        status: {
            connected: 'Connected',
            connecting: 'Connecting...',
            disconnected: 'Disconnected',
            error: 'Connection Failed',
        },

        // Backend task status
        taskStatus: {
            PENDING: 'Waiting to execute',
            STARTED: 'Executing',
            SUCCESS: 'Completed',
            FAILURE: 'Execution failed',
            RETRY: 'Retrying',
            REVOKED: 'Cancelled',
            INTERRUPTED: 'Interrupted',
        },

        // Thinking status
        thinking: {
            active: 'Thinking',
            completed: 'Thinking Complete',
        },

        // Agent flow
        agentFlow: {
            completed: 'Completed',
            processing: 'Processing',
            pending: 'Pending',
        },

        // Capabilities
        capabilities: {
            question: 'What can Hiro help with?',
        },

        // Empty state
        empty: {
            tryExamples: 'Quick Start - Try These Examples',
        },

        // Example questions
        examples: [
            'Help me draft an invention patent application for smartphone foldable screen technology',
            'Analyze the examination opinions in this OA notice',
            'Optimize the claim drafting to make it clearer and more complete',
            'Generate a specification based on the technical disclosure',
        ],

        // Input box
        input: {
            placeholder: {
                default: 'Type a message... Use {\'@\'} to add references, use / for quick commands',
                disabled: 'Input disabled, please wait...',
                sending: 'Sending message...',
            },
            placeholderDisabled: 'Please wait for Hiro to complete the current task...',
            placeholderWaiting: 'Waiting for response...',
            send: 'Send',
            stop: 'Stop',
            waiting: 'Processing',
            selectedText: 'Selected Text',
            clearSelection: 'Clear Selection',
            disabledFooter: 'Hiro is processing, please wait...',
            keys: {
                instruct: 'Instruct',
                generate: 'Generate',
                edit: 'Edit',
                upload: 'Upload',
            },
            workMode: {
                agent: 'Auto complete',
                copilot: 'Review each step',
            },
        },

        // File upload
        upload: {
            title: 'Upload Files',
            choose: 'Choose files to upload',
            hint: 'Drag and drop files here, or click to browse...',
            uploading: 'Uploading...',
            success: 'Upload successful',
            error: 'Upload failed, please try again',
            successMessage: 'File {fileName} uploaded successfully',
            uploadingMultiple: 'Uploading {total} files...',
            allSuccess: 'Successfully uploaded {count} files',
            partialSuccess: '{success} succeeded, {fail} failed',
            filePromptSuffix: 'Read these files, summarize how their content impacts the current task, and generate next step recommendations.',
        },

        // Chat history
        history: {
            title: 'Chat History',
            empty: 'No chat history',
            search: 'Search conversations',
            rename: 'Rename',
            delete: 'Delete',
            defaultTitle: 'New Chat',
            deleteConfirm: 'Are you sure you want to delete this conversation?',
            inputErrorMessage: 'Please enter a conversation title',
            // Date group labels
            today: 'Today',
            yesterday: 'Yesterday',
            lastWeek: 'Last 7 Days',
            lastMonth: 'Last 30 Days',
            older: 'Older',
        },

        // Action buttons
        actions: {
            copy: 'Copy',
            copyText: 'Copy as Text',
            copyMarkdown: 'Copy as Markdown',
            copyOptions: 'Copy Options',
            copySuccess: 'Copied to clipboard',
            copyFailed: 'Copy failed',
            noContentToCopy: 'No content to copy',
            exportToWord: 'Export to Word',
            exporting: 'Generating Word document...',
            exportSuccess: 'Word document downloaded',
            exportFailed: 'Export failed, please try again',
            newChat: 'New Chat',
            selectLanguage: 'Set AI Output Language',
            expandFullContent: 'Expand to view full content',
            enterFullscreen: 'Enter Fullscreen',
            exitFullscreen: 'Exit Fullscreen',
        },

        // Incomplete tasks reminder dialog
        incompleteTasksDialog: {
            title: 'Tasks Not Yet Complete',
            description: 'There are still incomplete steps in the current task plan:',
            bannerText: 'Task paused — incomplete steps remain.',
            completedLabel: 'Completed',
            incompleteLabel: 'Incomplete',
            continueBtn: 'Continue Tasks',
            endBtn: 'End Tasks',
            continueMessage: 'Please continue executing the remaining task steps',
            endMessage: 'Mark all tasks as completed. Then close the task.',
            inputPlaceholder: 'Additional notes (optional)',
        },

        // Messages
        messages: {
            sendFailed: 'Failed to send message, please try again',
            connecting: 'Connecting to chat...',
            applyingTemplate: 'Applying template...',
            templateAppliedSuccess: 'Successfully applied template: {name}',
            templateApplyFailed: 'Failed to apply template, please try again',
            editorNotInitialized: 'Editor not initialized, unable to apply template',
            modeChanged: 'Work mode changed successfully',
            modeChangeFailed: 'Failed to change work mode',
            modeWillBeSetOnSend: 'Work mode will be applied when you send the first message',
            loadHistoryFailed: 'Failed to load conversation history',
            createFailed: 'Failed to create conversation',
            deleteFailed: 'Failed to delete conversation',
            renameFailed: 'Failed to rename conversation',
            permissionDenied: 'You do not have permission to use {agentName}. Please contact sales to upgrade your plan.',
            permissionDeniedOverseas: 'You do not have permission to use {agentName}. Please {upgradeLink}.',
            upgradePlan: 'upgrade your plan',
            limitExceeded: 'Usage limit exceeded. Please upgrade your plan or try again tomorrow.',
            limitExceededOverseas: 'Usage limit exceeded. Please {upgradeLink} or try again tomorrow.',
            taskInProgress: 'Another task is in progress, please wait for it to complete before submitting.',
            globalTaskLimitExceeded: 'System is under high load, please try again later.',
            interruptFailed: 'Failed to interrupt task, please try again',
        },

        // Agent type names
        agentTypes: {
            disclosure: 'Disclosure',
            prosecution: 'Office Action Response',
            patent_draft: 'Patent Drafting',
        },

        // Selected text
        selectedText: {
            label: 'Selected Text:',
            clear: 'Clear selection',
        },

        // Quick actions
        quickActions: {
            draft: {
                title: 'Draft',
                icon: '🔍',
                description: 'Generate patent content and analyze prior art',
                actions: [
                    'Generate claims from disclosure',
                    'Write detailed description',
                    'Find related patents',
                ],
            },
            refine: {
                title: 'Refine',
                icon: '✏️',
                description: 'Strengthen arguments, structure, and polish',
                actions: [
                    'Spot weak arguments and suggest solutions',
                    'Add an executive summary',
                    'Find and fix typos',
                ],
            },
            format: {
                title: 'Format',
                icon: '🎨',
                description: 'Transform your document\'s structure with precision',
                actions: [
                    'Recommend formatting based on content',
                    'Add proper numbering to sections',
                    'Generate table of contents',
                ],
            },
        },

        // Instructions
        instructions: {
            select: 'Select Instruction',
            refresh: 'Refresh instruction list',
            searchPlaceholder: 'Search instructions...',
            noResults: 'No instructions found',
            suggestionsTitle: 'Recommended Instructions',
            useThis: 'Use this instruction',
            navigate: 'Go to Instruction Center',
            instructionContent: 'Instruction Content',
            description: 'Description',
            tags: 'Tags',
            jurisdiction: 'Jurisdiction',
            createdAt: 'Created At',
            exampleOutput: 'Example Output',
            close: 'Close',
        },

        // Pending state
        pending: {
            title: 'Need Your Input',
            subtitle: 'Hiro needs more information to continue',
            inputLabel: 'Your answer',
            waitingInfo: 'Waiting for your confirmation or input',
            tools: {
                askUser: 'Ask User',
                task: 'Task Confirmation',
                todoWrite: 'Todo Item',
            },
        },

        // Decision form
        decisionForm: {
            multiSelectHint: '(Multiple selection)',
            inputPlaceholder: 'Please enter...',
            questionAdditionalPlaceholder: 'If you have any additional notes, please enter here...',
            submitted: 'Submitted',
            submit: 'Submit',
            cancel: 'Cancel',
            loading: 'Loading decision form...',
            parseError: '⚠️ Decision form data format error',
        },

        // Task suggestions
        suggestions: {
            title: 'Task Suggestions',
            agentLabels: {
                novelty: 'Patent Novelty Search Agent',
            },
        },

        // Reference content
        reference: {
            label: 'Referenced selected content',
        },

        // Interrupting
        interrupting: {
            title: 'Stopping Execution',
            description: 'Safely terminating current task...',
            interrupted_by_user: 'Execution interrupted by user',
            processing: 'Processing...',
        },

        // Error messages
        error: {
            technicalDetails: 'Technical Details (Development Only)',
            errorCode: 'Error Code:',
            permissionDeniedReadOnly: 'Read-only permission: cannot send messages',
            permissionDeniedEdit: 'Read-only permission: editing is not allowed',
            permissionDeniedDelete: 'Insufficient permission to delete',
            permissionDeniedNoAccess: 'Access denied to this resource',
        },

        changesConfirmedContinue: 'Document changes have been confirmed. Please continue with the remaining tasks.',

        // Task list
        tasks: {
            completed: 'Completed',
            markComplete: 'Mark as complete',
            collapse: 'Collapse task list',
            expand: 'Expand to view all tasks',
        },

        // Tool call related
        toolCall: {
            result: 'View Tool Result',
            toolResultLabel: 'Tool Result',
            toolResultHint: 'This is an intermediate output from tool execution. Please refer to the version in "Documents" for the final content.',
            parameterDetails: 'Parameter Details',
            executing: 'Executing...',
            expand: 'Expand Details',
            collapse: 'Collapse',
        },

        // Tool names
        tools: {
            unknown: 'Tool Call',
            timeUnknown: '--:--',
            get_current_time: 'Get Current Time',
            create_document: 'Create Document',
            read_document: 'Read Document',
            list_project_attachments: 'List Project Attachments',
            read_attachment_content: 'Read Attachment Content',
            read_image: 'Read Image',
            auto_annotate_image: 'Auto Annotate Image',
            intelligent_patent_search: 'Intelligent Patent Search',
            get_patent_details: 'Get Patent Details',
            web_search: 'Web Search',
            patent_drafting_tool: 'Patent Drafting',
            patent_rule_review_tool: 'Patent Rule Review',
            extract_oa_and_generate_rejection_table: 'Extract OA Document and Generate Rejection Analysis Table',
            extract_oa_analysis: 'Extract OA Document and Generate Rejection Analysis Table',
            generate_oar_content: 'Generate OAR Content',
            fetch_office_action_pdf: 'Fetch Office Action PDF',
            analyze_disclosure: 'Analyze Disclosure',
            novelty_creativity_evaluation: 'Novelty Creativity Evaluation',
            update_plan: 'Update Plan',
            read_style: 'Read Writing Style',
            read_template: 'Read Template',
            request_user_decision: 'Request User Decision',
            load_skill: 'Load Skill',
            detect_and_setup_patent_office: 'Detect and Setup Patent Office',
            write_document: 'Write Document',
            end_task: 'End Task',
            suggest_next_step: 'Suggest Next Step',
            verify_office_action: 'Verify Office Action',
            analyze_oa_response_strategy: 'Analyze OA Response Strategy',
            streaming: 'Tool outputting...',
            done: 'Tool call completed',
        },

        // Patent search result renderer
        patentSearch: {
            found: 'Top {count} related patents',
            searchResult: 'Search Results',
            expand: 'Expand',
            collapse: 'Collapse',
            applicationDate: 'Application Date',
            publicationDate: 'Publication Date',
            relevance: 'Relevance',
            parseError: 'Failed to parse data',
            showDetails: 'Patent Details',
            noData: 'No data available',
            searchTypes: {
                semantic_search: 'Semantic Search',
                structured_query: 'Structured Search',
                patent_number: 'Patent Number Search',
            },
        },

        // Floating window
        floating: {
            minimize: 'Minimize',
            maximize: 'Maximize/Restore',
            close: 'Close',
        },

        // Patent detail renderer
        patentDetail: {
            basicInfo: 'Basic Information',
            patentNumber: 'Patent Number',
            title: 'Title',
            applicationNumber: 'Application Number',
            applicationDate: 'Application Date',
            publicationDate: 'Publication Date',
            inventor: 'Inventor',
            applicant: 'Applicant',
            abstract: 'Abstract',
            copySection: 'Copy Section',
            copyAll: 'Copy All',
            export: 'Export',
            copySuccess: 'Copied {section}',
            copyFailed: 'Copy failed',
            exportSuccess: 'Export successful',
            exportFailed: 'Export failed',
            images: 'Patent Images',
            parseError: 'Failed to parse data',
            fallbackHint: 'Below is the original content:',
            textFormatError: 'Current content is in text format, please view the full content in the conversation.',
        },

        // LLM Replay
        llmReplay: {
            title: 'Request Body',
            formView: 'Form View',
            jsonView: 'JSON View',
            basicConfig: 'Basic Configuration',
            model: 'Model',
            modelPlaceholder: 'e.g. claude-4.5-sonnet',
            temperature: 'Temperature',
            maxTokens: 'Max Tokens',
            stream: 'Stream',
            reasoningEffort: 'Reasoning Effort',
            selectPlaceholder: 'Please select',
            messageList: 'Messages',
            addMessage: 'Add Message',
            messageIndex: 'Message {index}',
            delete: 'Delete',
            role: 'Role',
            rolePlaceholder: 'Select role',
            content: 'Content',
            contentPlaceholder: 'Enter message content',
            jsonPlaceholder: 'Enter request body in JSON format',
            jsonError: 'JSON format error: {message}',
            switchError: 'Cannot switch: JSON format error',
            replayResult: 'Replay Result',
            fieldRole: 'Role',
            fieldContent: 'Content',
            finishReason: 'Finish Reason',
            tokenUsage: 'Token Usage',
            promptTokens: 'Prompt Tokens:',
            completionTokens: 'Completion Tokens:',
            totalTokens: 'Total:',
            modelInfo: 'Model Info',
            modelLabel: 'Model:',
            createdAt: 'Created At:',
            requestId: 'Request ID:',
            statusIdle: 'Idle',
            statusReplaying: 'Replaying',
            statusSuccess: 'Success',
            statusError: 'Failed',
        },
    },
};
